import { Cake, Dna, Mars, Microchip, Palette, Scissors, Venus } from 'lucide-react'

import type { Patient } from '@snootsvet/shared'
import { formatAge, formatCalendarDate, titleCase } from '@snootsvet/shared'

import { useViewerFormat } from '@/frontend/components/formatted-datetime'
import { SpeciesIcon } from '@/frontend/features/patient/species-icon'

export function usePatientAttributes(
  patient: Pick<
    Patient,
    'species' | 'sex' | 'isNeutered' | 'breed' | 'color' | 'dateOfBirth' | 'microchipNumber'
  >,
  { shortAge = false }: { shortAge?: boolean } = {}
) {
  const fmt = useViewerFormat()
  const isFemale = patient.sex.toLowerCase() === 'female'
  const SexIcon = isFemale ? Venus : Mars
  const neuter =
    patient.isNeutered === true
      ? isFemale
        ? 'Spayed'
        : 'Neutered'
      : patient.isNeutered === false
        ? 'Intact'
        : 'Unknown'
  const dob = patient.dateOfBirth ? formatCalendarDate(patient.dateOfBirth, fmt) : 'Unknown'
  const age = patient.dateOfBirth ? formatAge(patient.dateOfBirth, { short: shortAge }) : 'Unknown'

  return {
    species: {
      icon: <SpeciesIcon species={patient.species} className="text-current" />,
      label: 'Species',
      value: titleCase(patient.species),
    },
    breed: { icon: <Dna aria-hidden="true" />, label: 'Breed', value: patient.breed ?? 'Unknown' },
    color: {
      icon: <Palette aria-hidden="true" />,
      label: 'Colour',
      value: patient.color ?? 'Unknown',
    },
    dob: { icon: <Cake aria-hidden="true" />, label: 'Date of birth', value: dob },
    age: { icon: <Cake aria-hidden="true" />, label: 'Age', value: age },
    dobAndAge: {
      icon: <Cake aria-hidden="true" />,
      label: 'Date of birth and age',
      value: patient.dateOfBirth ? `${dob} (${age})` : 'Unknown',
    },
    sex: { icon: <SexIcon aria-hidden="true" />, label: 'Sex', value: titleCase(patient.sex) },
    neuter: { icon: <Scissors aria-hidden="true" />, label: 'Neuter status', value: neuter },
    microchip: {
      icon: <Microchip aria-hidden="true" />,
      label: 'Microchip',
      value: patient.microchipNumber ?? 'Unknown',
    },
  }
}
